import { ScrollArea } from "@/components/ui/scroll-area"
import { Button } from "@/components/ui/button"
import { X, FileCode, FileText, Image, Mic, Calendar, User, Clock, Tag, Link2, GitBranch, Loader2, FolderTree, RefreshCw, Bookmark, BookmarkX, Clipboard, Hash, ChevronRight, ChevronDown } from "lucide-react"
import { PersonLink } from "./person-link"
import { useState, useEffect, useCallback } from "react"
import { REGISTERED_EXTENSIONS } from "@/config/integrations"
import { useWorkspaceExtensions } from "@/hooks/use-workspace-extensions"
import { basecampApi } from "@/lib/basecamp-api"
import { toast } from "sonner"
import { fetchWithAuth, resolveDocumentRowId } from "@/lib/api"
import { useWorkspace } from "@/providers/workspace-provider"
import { resolveLinkageTodoId, isBasecampMessageDocument } from "@/lib/todo-id-from-path"
import { detectBasecampRecordType, resolveBasecampAppUrl } from "@/lib/basecamp-external-url"
import { BasecampNotesModal } from "@/components/integrations/basecamp/components/basecamp-notes-modal"
import { isReviewEnabled } from "@/lib/knowledge-hub-utils"
import { KnowledgeHubInfoModule } from "@/components/features/knowledge-hub/knowledge-hub-info-module"
import { DocumentInfoCopyChip } from "@/components/features/document-info-copy-chip"
import { testIds } from "@/lib/test-ids"

interface DocumentInfo {
  id: string
  title: string
  type: "work-spec" | "note" | "voice-memo" | "todo" | "flow" | "image"
  createdAt: string
  createdBy: string
  updatedAt: string
  updatedBy: string
  created_by?: string | null
  updated_by?: string | null
  tags: string[]
  assignee?: string
  dueDate?: string
  progress?: number
  status?: string
  wordCount?: number
  linkedDocumentsCount?: number
  version?: string
  // Enhanced fields from API
  createdAtFormatted?: string
  updatedAtFormatted?: string
  filePath?: string
  projectName?: string
  sourceUrl?: string
  organizationId?: string
  frontmatter?: Record<string, any>
}

interface DocumentInfoPaneProps {
  documentId: string
  /** Storage / pretty path when `documentId` may still be a path-keyed tab id. */
  documentPath?: string | null
  onClose: () => void
  onShowInBrowse?: (filePath: string) => void  // NEW: Handler to show document in browse sidebar
  /** When true, omit the panel header (Document Info dock owns chrome). */
  hideHeader?: boolean
  /** Open History mode in the Document Info dock. */
  onOpenHistory?: () => void
}

function isBasecampNotesPath(filePath?: string): boolean {
  return !!filePath && /basecamp-notes\.md$/i.test(filePath)
}

export function DocumentInfoPane({
  documentId,
  documentPath,
  onClose,
  onShowInBrowse,
  hideHeader = false,
  onOpenHistory,
}: DocumentInfoPaneProps) {
  const [relatedWorkSpec, setRelatedWorkSpec] = useState<{
    id: string;
    title: string;
    filePath: string;
  } | null>(null)
  const [info, setInfo] = useState<DocumentInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [isUpdating, setIsUpdating] = useState(false)
  const [isBookmarked, setIsBookmarked] = useState<boolean | null>(null)
  const [isTogglingBookmark, setIsTogglingBookmark] = useState(false)
  const [derivedProjectId, setDerivedProjectId] = useState<string | null>(null)
  const [derivedSourceUrl, setDerivedSourceUrl] = useState<string | null>(null)
  const [basecampNotesHtml, setBasecampNotesHtml] = useState<string | null>(null)
  const [showBasecampNotesModal, setShowBasecampNotesModal] = useState(false)
  const [isGeneratingHtml, setIsGeneratingHtml] = useState(false)
  /** Frontmatter dump starts collapsed for all document types (QA: reduce noise). */
  const [frontmatterExpanded, setFrontmatterExpanded] = useState(false)
  const { workspaces } = useWorkspace()
  const { isExtensionEnabled, isLoading: extensionsLoading } = useWorkspaceExtensions(workspaces[0]?.id ?? null)

  useEffect(() => {
    setFrontmatterExpanded(false)
  }, [documentId])

  useEffect(() => {
    const fetchDocumentInfo = async () => {
      if (!documentId || documentId === 'list') {
        setLoading(false)
        setInfo(null)
        return
      }

      // Handle Basecamp todo tabs - show Basecamp-specific info
      if (documentId.startsWith('basecamp-todo-')) {
        setLoading(false)
        // Extract todoId and projectId from documentId or integrationData
        // For now, set info to null - we'll create a Basecamp-specific info pane
        setInfo(null)
        return
      }

      // Skip fetching for other integration tabs (basecamp-project-, etc.)
      if (documentId.startsWith('basecamp-project-') || documentId.startsWith('integration-')) {
        setLoading(false)
        setInfo(null)
        setRelatedWorkSpec(null)
        return
      }

      setLoading(true)
      try {
        const pathFallback =
          documentPath?.trim() ||
          (documentId.includes('/') ? documentId : null)
        // Resolve pretty URL / path-keyed tabs to documents.id (slug -> storage root).
        const uuid = await resolveDocumentRowId(documentId, pathFallback, { workspaces })
        const response = await fetchWithAuth(`/api/documents/${uuid}`)
        if (response.ok) {
          const docData = await response.json()
          setInfo({
            id: docData.id,
            title: docData.title,
            type: docData.type,
            createdAt: docData.createdAtFormatted || docData.createdAt,
            createdBy: docData.createdBy || 'Unknown',
            updatedAt: docData.updatedAtFormatted || docData.updatedAt,
            updatedBy: docData.updatedBy || 'Unknown',
            tags: docData.tags || [],
            assignee: docData.assignee,
            dueDate: docData.dueDate,
            progress: docData.progress,
            status: docData.status,
            wordCount: docData.wordCount || 0,
            linkedDocumentsCount: docData.linkedDocumentsCount || 0,
            version: '1.0',
            // Enhanced fields
            created_by: docData.created_by ?? null,
            updated_by: docData.updated_by ?? null,
            createdAtFormatted: docData.createdAtFormatted,
            updatedAtFormatted: docData.updatedAtFormatted,
            filePath: docData.filePath,
            projectName: docData.projectName,
            organizationId: docData.organizationId,
            sourceUrl: (() => {
              const tid = resolveLinkageTodoId(docData.filePath, docData.frontmatter)
              const pid = docData.frontmatter?.project?.id || docData.frontmatter?.project_id
              const direct = docData.frontmatter?.url || docData.frontmatter?.source_url
              const candidate = typeof direct === 'string' && direct.length > 0 ? direct : null
              if (!tid || !pid) return candidate ?? undefined
              const recordType = detectBasecampRecordType(tid, {
                frontmatter: docData.frontmatter,
                filePath: docData.filePath,
              })
              return resolveBasecampAppUrl({
                recordId: tid,
                projectId: pid,
                recordType,
                candidateUrl: candidate,
              }) ?? undefined
            })(),
            frontmatter: docData.frontmatter || {},
          })
          setRelatedWorkSpec(null)
          const tid = resolveLinkageTodoId(docData.filePath, docData.frontmatter)
          if (tid) {
            try {
              const docsResponse = await fetchWithAuth(`/api/basecamp/todo-documents/${tid}?limit=5`)
              if (docsResponse.ok) {
                const docsData = await docsResponse.json()
                const workSpec = docsData.documents?.find((doc: any) =>
                  doc.type !== 'basecamp-notes' &&
                  (doc.type === 'work-spec' || (doc.filePath?.includes(`${tid}-`) && !doc.filePath?.includes('basecamp-notes')))
                )
                if (workSpec) {
                  setRelatedWorkSpec({
                    id: workSpec.id,
                    title: workSpec.title,
                    filePath: workSpec.filePath
                  })
                }
              }
            } catch (error) {
              console.error('[DocumentInfoPane] Failed to fetch related work spec:', error)
            }
          }
        } else {
          setInfo(null)
        }
      } catch (error) {
        console.error('Failed to fetch document info:', error)
        setInfo(null)
      } finally {
        setLoading(false)
      }
    }

    fetchDocumentInfo()
  }, [documentId, documentPath, workspaces])

  const effectiveTodoId = resolveLinkageTodoId(info?.filePath, info?.frontmatter)

  // When todo_id is from path, fetch projectId/sourceUrl from related docs or cached Basecamp todo
  useEffect(() => {
    if (!effectiveTodoId || info?.frontmatter?.project_id || info?.frontmatter?.project?.id) {
      setDerivedProjectId(null)
      setDerivedSourceUrl(null)
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const docsRes = await fetchWithAuth(`/api/basecamp/todo-documents/${effectiveTodoId}?limit=5`)
        const data = docsRes.ok ? await docsRes.json() : null
        if (!cancelled && data?.documents?.length) {
          const first = data.documents[0]
          const pid = first?.frontmatter?.project_id ?? first?.frontmatter?.project?.id
          const url = first?.frontmatter?.source_url ?? first?.frontmatter?.url
          if (pid) {
            setDerivedProjectId(pid)
            if (url) setDerivedSourceUrl(url)
            return
          }
        }
        const searchRes = await fetchWithAuth(`/api/basecamp/search-todo/${effectiveTodoId}`)
        const searchJson = searchRes.ok ? await searchRes.json() : null
        if (!cancelled && searchJson?.found && searchJson?.todo?.projectId) {
          setDerivedProjectId(String(searchJson.todo.projectId))
        }
      } catch {
        /* ignore */
      }
    })()
    return () => {
      cancelled = true
    }
  }, [effectiveTodoId, info?.frontmatter?.project_id, info?.frontmatter?.project?.id])

  // Load bookmark status when Basecamp todo info is available
  useEffect(() => {
    const loadBookmarkStatus = async () => {
      if (!info?.frontmatter && !effectiveTodoId) return;

      const projectId = info?.frontmatter?.project?.id ?? info?.frontmatter?.project_id ?? derivedProjectId
      const todoId = effectiveTodoId

      if (!todoId || !projectId) return;

      try {
        const status = await basecampApi.getBookmarkStatus(projectId.toString(), todoId.toString());
        setIsBookmarked(status.bookmarked);
      } catch (err) {
        console.warn('[DocumentInfoPane] Could not load bookmark status:', err);
        setIsBookmarked(null);
      }
    };

    loadBookmarkStatus();
  }, [effectiveTodoId, info?.frontmatter?.project?.id, info?.frontmatter?.project_id, derivedProjectId]);

  const handleToggleBookmark = async () => {
    if (isTogglingBookmark) return;

    const todoId = effectiveTodoId
    const projectId = info?.frontmatter?.project?.id ?? info?.frontmatter?.project_id ?? derivedProjectId
    const projectName = info?.frontmatter?.project?.name ?? 'Unknown Project'
    const todoTitle = info?.title ?? 'Untitled Todo'

    if (!todoId || !projectId) {
      toast.error('Missing Basecamp todo information', {
        description: 'Cannot bookmark: todo ID or project ID not found'
      });
      return;
    }

    const wasBookmarked = isBookmarked || false;
    const willBeBookmarked = !wasBookmarked;

    // OPTIMISTIC UPDATE: Update UI immediately
    setIsTogglingBookmark(true);
    setIsBookmarked(willBeBookmarked);

    // Optimistically update bookmarks widget
    window.dispatchEvent(new CustomEvent('basecamp-bookmark-changed', {
      detail: {
        todoId: todoId.toString(),
        projectId: projectId.toString(),
        projectName,
        title: todoTitle,
        added: willBeBookmarked,
        optimistic: true // Signal this is an optimistic update
      }
    }));

    // Make API call in background
    try {
      if (wasBookmarked) {
        await basecampApi.removeBookmark(projectId.toString(), todoId.toString());
      } else {
        await basecampApi.addBookmark(projectId.toString(), todoId.toString());
      }

      // Success - show subtle toast
      toast.success(willBeBookmarked ? 'Bookmarked' : 'Unbookmarked', {
        description: 'Synced with Basecamp',
        duration: 2000,
        position: 'bottom-right'
      });

      // Trigger sync to refresh from server (gets accurate data)
      window.dispatchEvent(new CustomEvent('basecamp-bookmark-changed', {
        detail: {
          todoId: todoId.toString(),
          projectId: projectId.toString(),
          synced: true // Signal sync is complete
        }
      }));
    } catch (err) {
      // Rollback on error
      console.error('[DocumentInfoPane] Error toggling bookmark:', err);
      setIsBookmarked(wasBookmarked); // Rollback

      // Rollback bookmarks widget
      window.dispatchEvent(new CustomEvent('basecamp-bookmark-changed', {
        detail: {
          todoId: todoId.toString(),
          projectId: projectId.toString(),
          added: wasBookmarked, // Rollback to original state
          optimistic: true
        }
      }));

      toast.error('Failed to sync bookmark', {
        description: err instanceof Error ? err.message : 'Unknown error',
        duration: 3000
      });
    } finally {
      setIsTogglingBookmark(false);
    }
  };

  const handleGenerateBasecampNotesHtml = useCallback(async () => {
    if (!documentId || !effectiveTodoId || !info) return
    setIsGeneratingHtml(true)
    try {
      const apiUrl = documentId.includes("/")
        ? `/api/documents/by-path?path=${encodeURIComponent(documentId)}`
        : `/api/documents/${documentId}`
      const response = await fetchWithAuth(apiUrl)
      if (!response.ok) throw new Error("Failed to fetch document content")
      const docData = await response.json()
      const fullContent = docData.content || ""
      const frontmatterMatch = fullContent.match(/^---\n([\s\S]*?)\n---\n([\s\S]*)$/)
      const markdownContent = frontmatterMatch ? frontmatterMatch[2] : fullContent
      const result = await basecampApi.generateBasecampNotesHTML(
        effectiveTodoId,
        markdownContent,
        info.frontmatter || docData.frontmatter || {}
      )
      setBasecampNotesHtml(result.html)
      setShowBasecampNotesModal(true)
    } catch (error) {
      console.error("[DocumentInfoPane] Error generating HTML:", error)
      toast.error("Failed to generate HTML", {
        description: error instanceof Error ? error.message : "Unknown error",
      })
    } finally {
      setIsGeneratingHtml(false)
    }
  }, [documentId, effectiveTodoId, info])

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!info) {
    return (
      <div className="h-full flex items-center justify-center">
        <p className="text-sm text-muted-foreground">No document selected</p>
      </div>
    )
  }

  const getIcon = (type: DocumentInfo["type"]) => {
    switch (type) {
      case "work-spec":
        return <FileCode className="h-4 w-4 text-blue-500" />
      case "voice-memo":
        return <Mic className="h-4 w-4 text-purple-500" />
      case "todo":
        return <FileText className="h-4 w-4 text-orange-500" />
      case "flow":
        return <GitBranch className="h-4 w-4 text-emerald-500" />
      case "image":
        return <Image className="h-4 w-4 text-sky-600" />
      default:
        return <FileText className="h-4 w-4 text-muted-foreground" />
    }
  }

  const getTypeLabel = (type: DocumentInfo["type"]) => {
    switch (type) {
      case "work-spec":
        return "Work Spec"
      case "voice-memo":
        return "Voice Memo"
      case "todo":
        return "Todo"
      case "flow":
        return "Flow"
      case "image":
        return "Image"
      default:
        return "Note"
    }
  }

  return (
    <div className="h-full min-w-0 max-w-full flex flex-col bg-card overflow-x-hidden">
      {!hideHeader ? (
        <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-border flex-shrink-0 min-w-0">
          <h3 className="text-sm font-semibold text-card-foreground flex items-center gap-2 min-w-0 truncate">
            {getIcon(info.type)}
            <span className="truncate">Document Info</span>
          </h3>
          <Button variant="ghost" size="icon" className="shrink-0" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      ) : null}

      {/* Content */}
      <ScrollArea className="flex-1 min-w-0 max-w-full overflow-hidden w-full">
        <div className="w-full min-w-0 max-w-full overflow-x-hidden box-border">
          <div className="p-4 space-y-4 min-w-0 w-full max-w-full overflow-x-hidden box-border">
          {REGISTERED_EXTENSIONS.filter(
            (ext) =>
              ext.documentInfoActions &&
              ext.enabled !== false &&
              (extensionsLoading || isExtensionEnabled(ext.id)),
          ).map((ext) => {
            const Actions = ext.documentInfoActions
            if (!Actions) return null
            return (
              <Actions
                key={ext.id}
                documentId={documentId}
                filePath={info?.filePath}
                frontmatter={info?.frontmatter}
                sourceUrl={info?.sourceUrl ?? derivedSourceUrl ?? null}
              />
            )
          })}

          {/* Related Work Spec - satellite doc shortcut only (not when already viewing that file) */}
          {effectiveTodoId && relatedWorkSpec && info?.id && relatedWorkSpec.id !== info.id && (
            <div className="pb-3 border-b border-border min-w-0 max-w-full">
              <div className="text-xs text-muted-foreground mb-1">Related Work Spec</div>
              <p className="text-xs text-muted-foreground/90 mb-2 leading-snug">
                Main todo file for this folder (open in a tab from here).
              </p>
              <Button
                variant="ghost"
                className="w-full justify-start gap-2 h-auto py-2 whitespace-normal min-w-0 max-w-full items-start"
                onClick={() => {
                  window.dispatchEvent(new CustomEvent('document-click', {
                    detail: { documentId: relatedWorkSpec.id }
                  }))
                }}
              >
                <FileText className="h-4 w-4 shrink-0 mt-0.5" />
                <div className="flex flex-col items-start flex-1 min-w-0 overflow-hidden">
                  <span className="text-sm font-medium break-words w-full">{relatedWorkSpec.title}</span>
                  <span className="text-xs text-muted-foreground break-all line-clamp-3 w-full">{relatedWorkSpec.filePath}</span>
                </div>
              </Button>
            </div>
          )}

          {/* Knowledge Hub module - prominent when review is opted in */}
          {(isReviewEnabled(info.frontmatter) ||
            info.frontmatter?.verified_by ||
            info.frontmatter?.verified_at ||
            info.frontmatter?.review_interval) && info.frontmatter ? (
            <KnowledgeHubInfoModule
              documentId={info.id}
              organizationId={info.organizationId}
              frontmatter={info.frontmatter}
              onFrontmatterChange={(next) => {
                setInfo((prev) => (prev ? { ...prev, frontmatter: next } : prev))
              }}
            />
          ) : null}

          {/* UUID + PATH: icon + width-aware value (no text labels); PATH narrow = basename */}
          <div className="space-y-1.5 min-w-0 max-w-full">
            {info.id ? (
              <DocumentInfoCopyChip
                label="UUID"
                value={info.id}
                icon={Hash}
                successToast="UUID copied"
                data-testid={testIds.documentInfoPane.uuid}
              />
            ) : null}
            {info.filePath ? (
              <DocumentInfoCopyChip
                label="Path"
                value={info.filePath.startsWith('/') ? info.filePath : `/${info.filePath}`}
                icon={FolderTree}
                displayMode="path"
                successToast="Path copied"
                onModifierClick={
                  onShowInBrowse ? () => onShowInBrowse(info.filePath!) : undefined
                }
                data-testid={testIds.documentInfoPane.path}
              />
            ) : null}
          </div>

          {/* Type: hide when KH review is opted in - status strip + KH module cover that layer;
              documents.type stays note (review is frontmatter, not a document_type enum). */}
          {!isReviewEnabled(info.frontmatter) && (
            <div>
              <div className="text-xs text-muted-foreground mb-1">TYPE</div>
              <div className="text-sm font-medium text-foreground">{getTypeLabel(info.type)}</div>
            </div>
          )}

          {/* Generic documents.status - hide when KH owns status via frontmatter.status */}
          {info.status && !isReviewEnabled(info.frontmatter) ? (
            <div>
              <div className="text-xs text-muted-foreground mb-1">STATUS</div>
              <div className="inline-flex items-center px-2 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium">
                {info.status}
              </div>
            </div>
          ) : null}

          {/* Progress */}
          {info.progress !== undefined && (
            <div>
              <div className="text-xs text-muted-foreground mb-2">PROGRESS</div>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary transition-all"
                    style={{ width: `${info.progress}%` }}
                  />
                </div>
                <span className="text-sm font-medium text-foreground">{info.progress}%</span>
              </div>
            </div>
          )}

          {/* Dates */}
          <div className="space-y-3">
            <div>
              <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                CREATED
              </div>
              <div className="text-sm text-foreground">{info.createdAt}</div>
              <div className="text-xs text-muted-foreground">
                by <PersonLink name={info.createdBy} userId={info.created_by} />
              </div>
            </div>

            <div>
              <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                <Clock className="h-3 w-3" />
                LAST UPDATED
              </div>
              <div className="text-sm text-foreground">{info.updatedAt}</div>
              <div className="text-xs text-muted-foreground">
                by <PersonLink name={info.updatedBy} userId={info.updated_by} />
              </div>
            </div>

            {info.dueDate && (
              <div>
                <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  DUE DATE
                </div>
                <div className="text-sm text-foreground">{info.dueDate}</div>
              </div>
            )}
          </div>

          {/* Assignee */}
          {info.assignee && (
            <div>
              <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                <User className="h-3 w-3" />
                ASSIGNEE
              </div>
              <div className="text-sm text-foreground">{info.assignee}</div>
            </div>
          )}

          {/* Tags */}
          {info.tags.length > 0 && (
            <div className="min-w-0 max-w-full">
              <div className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
                <Tag className="h-3 w-3 shrink-0" />
                TAGS
              </div>
              <div className="flex flex-wrap gap-1.5 min-w-0">
                {info.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-medium max-w-full break-all"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Metadata */}
          <div className="pt-3 border-t border-border space-y-2 min-w-0 max-w-full">
            <div className="text-xs text-muted-foreground mb-2">METADATA</div>

            {info.wordCount && (
              <div className="flex items-center justify-between gap-2 text-sm min-w-0">
                <span className="text-muted-foreground shrink-0">Word Count</span>
                <span className="text-foreground font-medium min-w-0 truncate text-right">
                  {info.wordCount.toLocaleString()}
                </span>
              </div>
            )}

            {info.linkedDocumentsCount !== undefined && (
              <div className="flex items-center justify-between gap-2 text-sm min-w-0">
                <span className="text-muted-foreground flex items-center gap-1 shrink-0">
                  <Link2 className="h-3 w-3" />
                  Linked Docs
                </span>
                <span className="text-foreground font-medium min-w-0 truncate text-right">
                  {info.linkedDocumentsCount}
                </span>
              </div>
            )}

            {info.version && (
              <div className="flex items-center justify-between gap-2 text-sm min-w-0">
                <span className="text-muted-foreground flex items-center gap-1 shrink-0">
                  <GitBranch className="h-3 w-3" />
                  Version
                </span>
                <span className="text-foreground font-medium min-w-0 truncate text-right">
                  {info.version}
                </span>
              </div>
            )}

            {onOpenHistory ? (
              <button
                type="button"
                data-testid={testIds.documentInfoPane.viewHistoryLink}
                onClick={onOpenHistory}
                className="flex w-full items-center justify-between gap-2 text-sm text-primary hover:underline"
              >
                <span className="flex items-center gap-1 shrink-0">
                  <Clock className="h-3 w-3" />
                  Version history
                </span>
                <span className="text-xs text-muted-foreground">View</span>
              </button>
            ) : null}
          </div>

          {/* Frontmatter - collapsed by default; expand to inspect raw fields */}
          {info.frontmatter && Object.keys(info.frontmatter).length > 0 && (
            <div className="pt-3 border-t border-border space-y-2">
              <button
                type="button"
                data-testid={testIds.documentInfoPane.frontmatterToggle}
                onClick={() => setFrontmatterExpanded((open) => !open)}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                aria-expanded={frontmatterExpanded}
              >
                {frontmatterExpanded ? (
                  <ChevronDown className="h-3 w-3 shrink-0" />
                ) : (
                  <ChevronRight className="h-3 w-3 shrink-0" />
                )}
                <span>FRONTMATTER</span>
              </button>
              {frontmatterExpanded ? (
                <div
                  data-testid={testIds.documentInfoPane.frontmatterSection}
                  className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-2 items-start min-w-0"
                >
                  {Object.entries(info.frontmatter)
                    .filter(([key]) => key !== 'source_url')
                    .map(([key, value], index) => {
                      let displayValue: unknown = value
                      if (typeof value === 'object' && value !== null) {
                        displayValue = JSON.stringify(value, null, 2)
                      } else if (typeof value === 'boolean') {
                        displayValue = value ? 'true' : 'false'
                      } else if (value === null || value === undefined) {
                        displayValue = '-'
                      }

                      return (
                        <div key={`frontmatter-${key}-${index}`} className="contents">
                          <span className="text-xs text-muted-foreground font-mono pt-0.5">{key}</span>
                          <span
                            className="text-xs text-foreground bg-muted px-2 py-1 rounded break-all whitespace-pre-wrap min-w-0"
                            style={{ wordBreak: 'break-all', overflowWrap: 'anywhere' }}
                          >
                            {String(displayValue)}
                          </span>
                        </div>
                      )
                    })}
                </div>
              ) : null}
            </div>
          )}

          {/* Basecamp Integration */}
          {(effectiveTodoId || info?.sourceUrl?.includes('basecamp.com') || derivedSourceUrl) && (
            <div className="pt-3 border-t border-border space-y-2">
              <div className="text-xs text-muted-foreground mb-2">BASECAMP</div>

              {isBasecampMessageDocument(info.filePath, info.frontmatter) && (
                <div
                  className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-800 dark:text-amber-200"
                  data-testid={testIds.documentInfoPane.messageStalenessBanner}
                >
                  <p className="font-medium">Message thread snapshot</p>
                  <p className="mt-1 text-muted-foreground">
                    New Basecamp replies are not synced automatically. Refresh from Basecamp after thread updates.
                  </p>
                  {info.frontmatter?.last_ingested_at && (
                    <p className="mt-1 text-muted-foreground">
                      Last ingested: {String(info.frontmatter.last_ingested_at).slice(0, 19).replace('T', ' ')}
                    </p>
                  )}
                </div>
              )}

              {/* Bookmark Button */}
              {effectiveTodoId && (info?.frontmatter?.project?.id ?? info?.frontmatter?.project_id ?? derivedProjectId) && (
                <Button
                  variant={isBookmarked ? "secondary" : "outline"}
                  size="sm"
                  className="w-full justify-start gap-2"
                  disabled={isTogglingBookmark || isBookmarked === null}
                  onClick={handleToggleBookmark}
                >
                  {isTogglingBookmark ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {isBookmarked ? 'Removing...' : 'Adding...'}
                    </>
                  ) : isBookmarked ? (
                    <>
                      <BookmarkX className="h-4 w-4" />
                      Remove Bookmark
                    </>
                  ) : (
                    <>
                      <Bookmark className="h-4 w-4" />
                      Add Bookmark
                    </>
                  )}
                </Button>
              )}

              {info.sourceUrl && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-start gap-2"
                  disabled={isUpdating}
                  onClick={async () => {
                    setIsUpdating(true);
                    try {
                      const { toast } = await import('sonner');

                      // Show confirmation
                      const isMessageDoc = isBasecampMessageDocument(info.filePath, info.frontmatter);
                      const confirmToast = toast.info(
                        isMessageDoc ? 'Refresh message from Basecamp?' : 'Update work spec from Basecamp?',
                        {
                        description: isMessageDoc
                          ? 'Re-fetch the message and all thread comments into this file'
                          : 'Re-fetch latest data and update this file',
                        duration: 10000,
                        action: {
                          label: 'Update',
                          onClick: async () => {
                            toast.dismiss(confirmToast);

                            const loadingToast = toast.loading('Updating from Basecamp...', {
                              description: 'Fetching latest data'
                            });

                            try {
                              const isMessage = isBasecampMessageDocument(info.filePath, info.frontmatter);
                              const refreshUrl = info.sourceUrl || derivedSourceUrl;
                              if (!refreshUrl) {
                                throw new Error('No Basecamp URL on this document');
                              }
                              if (isMessage) {
                                const { ingestBasecampMessage } = await import('../../lib/api');
                                const result = await ingestBasecampMessage(refreshUrl, { forceRefresh: true });
                                toast.success('Message refreshed from Basecamp', {
                                  id: loadingToast,
                                  description: result.title ? `Updated: ${result.title}` : 'Thread comments re-fetched',
                                  duration: 3000,
                                });
                              } else {
                                const {
                                  ingestBasecampTodo,
                                  applyUpdateWorkSpecModeUi,
                                  handleUpdateWorkSpecBlockedError,
                                } = await import('../../lib/api');
                                const result = await ingestBasecampTodo(refreshUrl, { forceRefresh: true });
                                applyUpdateWorkSpecModeUi(result);
                                toast.success('Work spec updated!', {
                                  id: loadingToast,
                                  description: `Updated: ${result.title}`,
                                  duration: 3000,
                                });
                              }

                              // Reload document info
                              setTimeout(() => window.location.reload(), 1000);
                            } catch (error: any) {
                              const { handleUpdateWorkSpecBlockedError } = await import('../../lib/api');
                              if (handleUpdateWorkSpecBlockedError(error)) {
                                toast.dismiss(loadingToast);
                                return;
                              }
                              toast.error('Update failed', {
                                id: loadingToast,
                                description: error.message || 'Failed to update'
                              });
                            } finally {
                              setIsUpdating(false);
                            }
                          }
                        },
                        cancel: {
                          label: 'Cancel',
                          onClick: () => {
                            toast.dismiss(confirmToast);
                            setIsUpdating(false);
                          }
                        }
                      });
                    } catch (error: any) {
                      const { toast } = await import('sonner');
                      toast.error('Update failed', {
                        description: error.message
                      });
                      setIsUpdating(false);
                    }
                  }}
                >
                  {isUpdating ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-4 w-4" />
                      {isBasecampMessageDocument(info.filePath, info.frontmatter)
                        ? 'Refresh from Basecamp'
                        : 'Update from Basecamp'}
                    </>
                  )}
                </Button>
              )}

              {effectiveTodoId && isBasecampNotesPath(info.filePath) && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-start gap-2"
                  disabled={isGeneratingHtml}
                  onClick={() => void handleGenerateBasecampNotesHtml()}
                >
                  {isGeneratingHtml ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Generating…
                    </>
                  ) : (
                    <>
                      <Clipboard className="h-4 w-4" />
                      Generate Basecamp-ready HTML
                    </>
                  )}
                </Button>
              )}
            </div>
          )}

          </div>
        </div>
      </ScrollArea>

      {effectiveTodoId && (
        <BasecampNotesModal
          todoId={effectiveTodoId}
          html={basecampNotesHtml || undefined}
          open={showBasecampNotesModal}
          onOpenChange={setShowBasecampNotesModal}
        />
      )}
    </div>
  )
}

